import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  ChevronDown,
  FileText,
  Lock,
  ShieldCheck,
  User as UserIcon,
} from "lucide-react";
import axiosInstance from "../../../api/Axiosinstance";
import { ENDPOINTS } from "../../../api/Endpoints";
import providersService from "../../../api/services/providersService";
import { useToast } from "../../../hooks/useToast";
import {
  POLICY_EFFECTIVE_DATE,
  PRIVACY_POLICY_ITEMS,
  TERMS_CONDITION_ITEMS,
} from "../../../data/settingsPolicyContent";
import PasswordField from "../../../components/settings/PasswordField";
import PolicyPanel from "../../../components/settings/PolicyPanel";

import PersonalPanel from "./settings/PersonalPanel";
import ExperiencePanel from "./settings/ExperiencePanel";
import LicensePanel from "./settings/LicensePanel";
import VehiclePanel from "./settings/VehiclePanel";
import PricingPanel from "./settings/PricingPanel";
import useAppConfig from "../../../hooks/useAppConfig";

const settingsTabs = [
  {
    key: "verification",
    label: "Profile Verification",
    icon: UserIcon,
    to: "/dashboards/assistant/settings",
  },
  {
    key: "password",
    label: "Change Password",
    icon: Lock,
    to: "/dashboards/assistant/settings/password",
  },
  {
    key: "privacy",
    label: "Privacy Policy",
    icon: ShieldCheck,
    to: "/dashboards/assistant/settings/privacy",
  },
  {
    key: "terms",
    label: "Terms & Condition",
    icon: FileText,
    to: "/dashboards/assistant/settings/terms",
  },
];

const verificationSections = [
  { key: "personal", label: "Personal Information" },
  { key: "experience", label: "Experience" },
  { key: "license", label: "License" },
  { key: "vehicle", label: "Vehicle Information" },
  { key: "pricing", label: "Pricing & Account" },
];

/**
 * Show the assistant what they last submitted.
 *
 * After approval, edits are staged rather than written to the live profile, so
 * reading the raw profile back would show the old values and make a saved
 * change look lost. The staged patch is layered on top for display only.
 */
const withPendingChanges = (profile) => {
  const staged = profile?.pendingChanges?.data;
  if (!staged) return profile;
  return { ...profile, ...staged };
};

const unwrapProfile = (response) => {
  if (!response) return {};
  return (
    response.data?.data?.profile ||
    response.data?.data?.user ||
    response.data?.data ||
    response.data?.profile ||
    response.data ||
    response.profile ||
    response
  );
};

export default function Settings() {
  const toast = useToast();
  const { section } = useParams();
  const navigate = useNavigate();
  const { data: config } = useAppConfig();

  const privacyItems = config?.privacyPolicy?.items?.length > 0 ? config.privacyPolicy.items : PRIVACY_POLICY_ITEMS;
  const privacyDate = config?.privacyPolicy?.lastUpdated ? new Date(config.privacyPolicy.lastUpdated).toLocaleDateString() : POLICY_EFFECTIVE_DATE;
  const termsItems = config?.termsAndConditions?.items?.length > 0 ? config.termsAndConditions.items : TERMS_CONDITION_ITEMS;
  const termsDate = config?.termsAndConditions?.lastUpdated ? new Date(config.termsAndConditions.lastUpdated).toLocaleDateString() : POLICY_EFFECTIVE_DATE;

  const normalizedSection = (section || "").toLowerCase();
  const verificationKeys = useMemo(
    () => verificationSections.map((item) => item.key),
    []
  );

  const activeTab = ["password", "privacy", "terms"].includes(normalizedSection)
    ? normalizedSection
    : "verification";

  const requestedAccordion = verificationKeys.includes(normalizedSection)
    ? normalizedSection
    : "personal";

  const [openAccordion, setOpenAccordion] = useState(requestedAccordion);
  const [profile, setProfile] = useState({});


  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [isPasswordSaving, setIsPasswordSaving] = useState(false);

  const [reviewModalSection, setReviewModalSection] = useState(null);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  const queryClient = useQueryClient();

  const fetchSettingsData = async () => {
    try {
      const profileData = await providersService.getProfile();
      setProfile(profileData);
    } catch (err) {
      console.error("Failed to fetch profile:", err);
    }
  };

  useEffect(() => {
    fetchSettingsData();
  }, []);

  useEffect(() => {
    if (activeTab === "verification") {
      setOpenAccordion(requestedAccordion);
    }
  }, [activeTab, requestedAccordion]);

  const toggleAccordion = (key) => {
    const nextKey = openAccordion === key ? null : key;
    setOpenAccordion(nextKey);
    navigate(nextKey ? `/dashboards/assistant/settings/${nextKey}` : "/dashboards/assistant/settings");
  };

  const handleRequestReview = (sectionKey, saveFn) => {
    setReviewModalSection({ key: sectionKey, saveFn });
  };

  const handleReviewSubmit = async () => {
    if (!reviewModalSection) return;
    setIsSubmittingReview(true);

    try {
      const success = await reviewModalSection.saveFn();
      if (success) {
        try {
          const res = await providersService.submitProfileReview();
          const refreshedProfile = unwrapProfile(res);
          if (refreshedProfile && Object.keys(refreshedProfile).length) {
            setProfile(refreshedProfile);
          } else {
            await fetchSettingsData();
          }
        } catch (reviewErr) {
          console.error("Failed to submit profile for review:", reviewErr);
        }

        // Settings keeps its own copy of the profile; without this the
        // dashboard keeps showing the pre-submission state until its cache
        // expires.
        queryClient.invalidateQueries({ queryKey: ["profile"] });
        queryClient.invalidateQueries({ queryKey: ["expertise"] });

        setReviewModalSection(null);
      }
    } finally {
      setIsSubmittingReview(false);
    }
  };

  // Panels render what the assistant last submitted, pending or not.
  const displayProfile = useMemo(() => withPendingChanges(profile), [profile]);
  const hasPendingChanges = profile?.pendingChanges?.status === "pending";
  const changesRejected = profile?.pendingChanges?.status === "rejected";

  const passwordFieldsComplete = Boolean(
    passwordForm.currentPassword &&
      passwordForm.newPassword &&
      passwordForm.confirmPassword
  );

  const passwordMismatch =
    Boolean(passwordForm.newPassword && passwordForm.confirmPassword) &&
    passwordForm.newPassword !== passwordForm.confirmPassword;

  const handlePasswordSave = async () => {
    if (!passwordFieldsComplete) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (passwordMismatch) {
      toast.error("Password does not match.");
      return;
    }

    setIsPasswordSaving(true);
    try {
      await axiosInstance.post(ENDPOINTS.AUTH.CHANGE_PASSWORD, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword,
      });
      toast.success("Password updated successfully.");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.apiError(error, "Unable to update password.");
    } finally {
      setIsPasswordSaving(false);
    }
  };

  const renderVerificationSection = (key) => {
    switch (key) {
      case "personal":
        return (
          <PersonalPanel
            profile={displayProfile}
            onRequestReview={handleRequestReview}
          />
        );
      case "experience":
        return (
          <ExperiencePanel
            profile={displayProfile}
            onRequestReview={handleRequestReview}
          />
        );
      case "license":
        return (
          <LicensePanel
            profile={displayProfile}
            onRequestReview={handleRequestReview}
          />
        );
      case "vehicle":
        return (
          <VehiclePanel
            profile={displayProfile}
            onRequestReview={handleRequestReview}
          />
        );
      case "pricing":
        return (
          <PricingPanel
            profile={displayProfile}
            onRequestReview={handleRequestReview}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex w-full max-w-none flex-col gap-4 pb-10 pt-2">
      {hasPendingChanges ? (
        <div className="rounded-xl border border-[#B9DCF7] bg-[#EFF8FF] px-4 py-3 text-[#0B4A6F]">
          <p className="text-base font-semibold">Changes under review</p>
          <p className="mt-0.5 text-sm font-medium leading-6">
            The details below are what you submitted. They go live on your public
            profile once the super admin approves them — until then your existing
            profile stays visible and you keep receiving bookings.
          </p>
        </div>
      ) : null}

      {changesRejected ? (
        <div className="rounded-xl border border-[#F4D28D] bg-[#FFF6E5] px-4 py-3 text-[#B26700]">
          <p className="text-base font-semibold">Changes not approved</p>
          <p className="mt-0.5 text-sm font-medium leading-6">
            {profile?.pendingChanges?.adminFeedback ||
              "The updates you submitted were not approved. Adjust them below and submit again."}
          </p>
        </div>
      ) : null}

      <div className="rounded-2xl border border-[#EEF0F3] bg-white p-3 sm:p-4">
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {settingsTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.key === activeTab;
            return (
              <NavLink
                key={tab.key}
                to={tab.to}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-3 text-base font-medium transition ${
                  isActive
                    ? "bg-[#61013B] text-white"
                    : "text-[#475467] hover:bg-[#F9FAFB]"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </NavLink>
            );
          })}
        </div>
      </div>

      {activeTab === "verification" ? (
        <div className="space-y-4">
          {verificationSections.map((item) => {
            const isOpen = openAccordion === item.key;
            return (
              <div key={item.key} className="rounded-2xl border border-[#EEF0F3] bg-white">
                <button
                  type="button"
                  onClick={() => toggleAccordion(item.key)}
                  className="flex w-full items-center justify-between px-5 py-5 text-left text-lg font-semibold text-[#1D2939]"
                >
                  <span>{item.label}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-[#1D2939] transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen ? (
                  <div className="border-t border-[#EEF0F3] px-5 py-5">
                    {renderVerificationSection(item.key)}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {activeTab === "password" ? (
        <div className="rounded-2xl border border-[#EEF0F3] bg-white p-5 sm:p-6">
          <div className="space-y-6">
            <p className="text-base font-medium text-[#667085]">
              Create a new password you can remember easily.
            </p>

            <div className="space-y-5">
              <PasswordField
                label="Current Password"
                value={passwordForm.currentPassword}
                show={showPassword.currentPassword}
                onToggle={() =>
                  setShowPassword((prev) => ({
                    ...prev,
                    currentPassword: !prev.currentPassword,
                  }))
                }
                autoComplete="current-password"
                onChange={(value) => {
                  setPasswordForm((prev) => ({ ...prev, currentPassword: value }));
                }}
              />
              <PasswordField
                label="New Password"
                value={passwordForm.newPassword}
                show={showPassword.newPassword}
                onToggle={() =>
                  setShowPassword((prev) => ({
                    ...prev,
                    newPassword: !prev.newPassword,
                  }))
                }
                autoComplete="new-password"
                onChange={(value) => {
                  setPasswordForm((prev) => ({ ...prev, newPassword: value }));
                }}
              />
              <PasswordField
                label="Repeat Password"
                value={passwordForm.confirmPassword}
                show={showPassword.confirmPassword}
                onToggle={() =>
                  setShowPassword((prev) => ({
                    ...prev,
                    confirmPassword: !prev.confirmPassword,
                  }))
                }
                autoComplete="new-password"
                onChange={(value) => {
                  setPasswordForm((prev) => ({ ...prev, confirmPassword: value }));
                }}
              />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handlePasswordSave}
                disabled={!passwordFieldsComplete || isPasswordSaving}
                className="rounded-xl bg-[#61013B] px-5 py-3 text-base font-semibold text-white transition hover:bg-[#4d0130] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isPasswordSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === "privacy" ? (
        <PolicyPanel
          title="Privacy Policy"
          items={privacyItems}
          lastUpdated={privacyDate}
          editable={false}
        />
      ) : null}

      {activeTab === "terms" ? (
        <PolicyPanel
          title="Terms & Condition"
          items={termsItems}
          lastUpdated={termsDate}
          editable={false}
        />
      ) : null}

      {reviewModalSection ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#FFF6E5] text-[#F5A524]">
              <AlertCircle className="h-7 w-7" strokeWidth={2} />
            </div>

            <h2 className="mt-4 text-xl font-semibold text-[#7A054A]">
              Submit Changes for Review
            </h2>

            <p className="mt-3 text-sm leading-relaxed text-[#667085]">
              You have made updates to your account details. To ensure quality and
              safety, these changes must be reviewed and approved by an admin before
              they go live. During this time your account may be temporarily marked as{" "}
              <span className="font-semibold text-[#475467]">Pending Verification.</span>
            </p>

            <p className="mt-2 text-sm leading-relaxed text-[#667085]">
              We will notify you when the review is complete.
            </p>

            <div className="mt-6 flex flex-col gap-2.5 w-full">
              <button
                type="button"
                onClick={handleReviewSubmit}
                disabled={isSubmittingReview}
                className="rounded-xl bg-[#61013B] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#4d0130] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSubmittingReview ? "Submitting..." : "Submit for Review"}
              </button>
              <button
                type="button"
                onClick={() => setReviewModalSection(null)}
                disabled={isSubmittingReview}
                className="rounded-xl border border-[#7A054A] bg-white px-5 py-2.5 text-sm font-semibold text-[#7A054A] transition hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-70"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
